// DS: MO2.4.1
import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { Url } from '@wix/da-url';
import {
  BiData,
  type BiEvent,
  BuyCoreButtonBiEvent,
} from '@wix/da-bi/pkg/events';
import { Promo } from '@wix/da-react-context/pkg/publicSession';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import TextButton, {
  type TextButtonProps,
} from '@wix/da-ds/pkg/Buttons/TextButton';
import { useCurrentPromo } from '../../../../utils/hooks/useCurrentPromo';
import SaleIndicator, {
  type SaleIndicatorProps,
} from '../../lego/SaleIndicator';
import s from './BuyCoreTextButton.scss';

interface Props
  extends Omit<
    TextButtonProps,
    'variant' | 'target' | 'href' | 'onClick' | 'biData'
  > {
  promo?: Promo;
  onClick?: () => void;
  suppressPromo?: boolean;
  withSaleSparkles?: SaleIndicatorProps['withSparkles'];
  biData?: BiEvent;

  /**
   * Upon clicking this button, the user will be directed to purchase core. Upon completing that flow, they'll be returned
   * to whatever URL is passed in this prop.
   */
  referer?: string;
}

const BuyCoreTextButton: React.FC<Props> = ({
  promo,
  size,
  referer,
  biData,
  children,
  suppressPromo,
  withSaleSparkles,
  ...rest
}) => {
  const { t } = useTranslation();
  const isMobile = useContext(MobileContext);
  const currentPromo = useCurrentPromo('self') || promo;
  const buttonLabel = isMobile ? t('common.getCore') : children;

  return (
    <TextButton
      size={size}
      variant="core-orange"
      target="_blank"
      href={Url.coreMembershipLink({ referer })}
      biData={BiData<BuyCoreButtonBiEvent>({
        evid: 375,
        ...biData,
      } as any)}
      noHorizontalPaddings={false}
      {...rest}
    >
      {buttonLabel}
      {!suppressPromo && currentPromo?.discountValuePercent && (
        <SaleIndicator
          discount={currentPromo.discountValuePercent}
          size={size === 'small' ? 'small' : 'medium'}
          background="themed"
          className={s['sale']}
          withSparkles={withSaleSparkles}
        />
      )}
    </TextButton>
  );
};

BuyCoreTextButton.displayName = 'BuyCoreTextButton';

export default BuyCoreTextButton;
